// USDA Market News on a product's page (P4, decision 144): the latest Los Angeles terminal and Phoenix shipping-point
// ranges for its commodity beside our cost, the weeks before, and every kind of row USDA reported. The office links the
// product to its commodity once (suggested from its name); a variety and a package narrow it when it matters.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { formatDate, formatMoney } from '../format';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';
import { ErrorBox } from './ui';

export function UsdaCard({ itemId, editable }: { itemId: string; editable: boolean }) {
  const { t, intlLocale } = useLocale();
  const qc = useQueryClient();
  const data = useQuery({ queryKey: qk.itemUsda(itemId), queryFn: ({ signal }) => api.itemUsda(itemId, signal) });
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState('');
  const [commodity, setCommodity] = useState('');
  const [variety, setVariety] = useState('');
  const [pkg, setPkg] = useState('');
  const [all, setAll] = useState(false);
  const list = useQuery({ queryKey: ['usda', 'commodities', q], queryFn: ({ signal }) => api.usdaCommodities(q, signal), enabled: editing });
  const link = useMutation({
    mutationFn: () => api.linkUsda(itemId, { commodity, variety: variety || null, package: pkg || null }),
    onSuccess: (u) => {
      qc.setQueryData(qk.itemUsda(itemId), u);
      void qc.invalidateQueries({ queryKey: ['pricing'] });
      setEditing(false);
    },
  });
  const unlink = useMutation({
    mutationFn: () => api.unlinkUsda(itemId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.itemUsda(itemId) }),
  });
  const money = (n: number | null) => (n === null ? '—' : formatMoney(n, intlLocale));
  const range = (lo: number | null, hi: number | null) => (lo === null && hi === null ? '—' : lo === hi || hi === null ? money(lo) : lo === null ? money(hi) : `${money(lo)} – ${money(hi)}`);
  const u = data.data;
  const picked = list.data?.find((c) => c.commodity === commodity);
  const start = () => {
    setCommodity(u?.link?.commodity ?? u?.suggestions[0] ?? '');
    setVariety(u?.link?.variety ?? '');
    setPkg(u?.link?.package ?? '');
    setQ('');
    setEditing(true);
  };

  return (
    <section className="card stack-sm" aria-labelledby="usda-h" data-testid="usda-card">
      <div className="card-head">
        <h2 id="usda-h">{t('usdaBlock')}</h2>
        {editable && u && !editing ? (
          <button type="button" className="btn btn-ghost" onClick={start}>
            {u.link ? t('usdaChange') : t('usdaLink')}
          </button>
        ) : null}
      </div>
      <p className="muted">{t('usdaHint')}</p>
      {data.error ? <ErrorBox error={data.error} /> : null}
      {u && !u.configured ? <p className="muted">{t('usdaNotSetUp')}</p> : null}
      {u && !u.link && !editing ? <p className="muted">{u.suggestions.length ? `${t('usdaSuggested')} ${u.suggestions.join(', ')}` : t('usdaNotLinked')}</p> : null}
      {editing ? (
        <div className="stack-sm usda-link">
          {u?.suggestions.length ? (
            <div className="chips" role="group" aria-label={t('usdaSuggested')}>
              {u.suggestions.map((s) => (
                <button key={s} type="button" className="chip" aria-pressed={commodity === s} onClick={() => setCommodity(s)}>
                  {s}
                </button>
              ))}
            </div>
          ) : null}
          <label className="field">
            <span>{t('usdaSearch')}</span>
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <label className="field">
            <span>{t('usdaCommodity')}</span>
            <select value={commodity} onChange={(e) => setCommodity(e.target.value)}>
              <option value="">—</option>
              {commodity && !list.data?.some((c) => c.commodity === commodity) ? <option value={commodity}>{commodity}</option> : null}
              {(list.data ?? []).map((c) => (
                <option key={c.commodity} value={c.commodity}>
                  {c.commodity}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>{t('usdaVariety')}</span>
            <input list="usda-varieties" value={variety} onChange={(e) => setVariety(e.target.value)} placeholder={t('usdaAny')} />
            <datalist id="usda-varieties">{(picked?.varieties ?? []).map((v) => <option key={v} value={v} />)}</datalist>
          </label>
          <label className="field">
            <span>{t('usdaPackage')}</span>
            <input list="usda-packages" value={pkg} onChange={(e) => setPkg(e.target.value)} placeholder={t('usdaAny')} />
            <datalist id="usda-packages">{(picked?.packages ?? []).map((v) => <option key={v} value={v} />)}</datalist>
          </label>
          {link.error ? <ErrorBox error={link.error} /> : null}
          <div className="quick-grid">
            <button type="button" className="btn btn-ghost" onClick={() => setEditing(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn btn-primary" disabled={!commodity || link.isPending} onClick={() => link.mutate()}>
              {t('save')}
            </button>
          </div>
          {u?.link ? (
            <button type="button" className="btn btn-ghost" onClick={() => { if (!window.confirm(`${t('usdaUnlink')}: ${t('confirmSure')}`)) return; unlink.mutate(); setEditing(false); }}>
              {t('usdaUnlink')}
            </button>
          ) : null}
        </div>
      ) : null}
      {u?.link && !editing ? (
        <>
          <p>
            <strong>{u.link.commodity}</strong>
            {u.link.variety ? ` · ${u.link.variety}` : ''}
            {u.link.package ? ` · ${u.link.package}` : ''}
          </p>
          {u.latest.length === 0 ? (
            <p className="muted">{t('usdaNoPrices')}</p>
          ) : (
            <div className="fc-tiles">
              {u.latest.map((l) => (
                <div key={l.market} className="fc-tile">
                  <span className="fc-tile-label">{l.market}</span>
                  <strong className="fc-tile-value">{range(l.low, l.high)}</strong>
                  <span className="muted">
                    {l.package ?? ''} · {formatDate(l.date, intlLocale, 'monthDay')}
                  </span>
                </div>
              ))}
              {u.ourCost !== null ? (
                <div className="fc-tile">
                  <span className="fc-tile-label">{t('usdaOurCost')}</span>
                  <strong className="fc-tile-value">{money(u.ourCost)}</strong>
                  <span className="muted">{t('usdaOurCostHint')}</span>
                </div>
              ) : null}
            </div>
          )}
          {u.weeks.length ? (
            <div className="table-scroll">
              <table className="data-table" aria-label={t('usdaWeeks')}>
                <thead>
                  <tr>
                    <th scope="col">{t('usdaWeekOf')}</th>
                    {[...new Set(u.weeks.map((w) => w.market))].map((m) => (
                      <th key={m} scope="col" className="num">
                        {m}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...new Set(u.weeks.map((w) => w.week))].reverse().map((wk) => (
                    <tr key={wk}>
                      <td>{formatDate(wk, intlLocale, 'monthDay')}</td>
                      {[...new Set(u.weeks.map((w) => w.market))].map((m) => (
                        <td key={m} className="num">
                          {money(u.weeks.find((w) => w.week === wk && w.market === m)?.mid ?? null)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {u.rows.length ? (
            <button type="button" className="btn btn-ghost" aria-expanded={all} onClick={() => setAll(!all)}>
              {all ? t('usdaHideRows') : `${t('usdaShowRows')} (${u.rows.length})`}
            </button>
          ) : null}
          {all ? (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th scope="col">{t('usdaMarket')}</th>
                    <th scope="col">{t('usdaVariety')}</th>
                    <th scope="col">{t('usdaPackage')}</th>
                    <th scope="col">{t('usdaSize')}</th>
                    <th scope="col">{t('usdaOrigin')}</th>
                    <th scope="col" className="num">{t('usdaRange')}</th>
                    <th scope="col" className="num">{t('usdaMostly')}</th>
                    <th scope="col">{t('usdaDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {u.rows.map((r, i) => (
                    <tr key={i}>
                      <td>{r.market}</td>
                      <td>{r.variety ?? ''}{r.organic ? ' (org.)' : ''}</td>
                      <td>{r.package ?? ''}</td>
                      <td>{r.size ?? ''}</td>
                      <td>{r.origin ?? ''}</td>
                      <td className="num">{range(r.low, r.high)}</td>
                      <td className="num">{r.mostlyLow === null && r.mostlyHigh === null ? '' : range(r.mostlyLow, r.mostlyHigh)}</td>
                      <td>{formatDate(r.date, intlLocale, 'monthDay')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
